/**
 * Client-side PDF text extraction utility
 * Uses PDF.js library to extract text from PDF files in the browser
 */

// We'll use the CDN version of PDF.js to avoid bundling it
declare global {
  interface Window {
    pdfjsLib: any;
  }
}

// Load PDF.js from CDN if not already loaded
export async function loadPdfJs(): Promise<void> {
  if (window.pdfjsLib) {
    return; // Already loaded
  }

  return new Promise((resolve, reject) => {
    // Create script element for PDF.js
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
    script.onload = () => {
      // Set worker source
      window.pdfjsLib.GlobalWorkerOptions.workerSrc = 
        'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
      resolve();
    };
    script.onerror = () => reject(new Error('Failed to load PDF.js'));
    document.head.appendChild(script);
  });
}

/**
 * Extract text content from a PDF file
 * @param file The PDF file to extract text from
 * @returns The extracted text content
 */
export async function extractTextFromPdf(file: File): Promise<string> {
  console.log('🔍 [PDF Extraction] Starting PDF text extraction for:', file.name);
  console.log('📊 [PDF Extraction] File size:', (file.size / 1024).toFixed(2), 'KB');
  
  try {
    // Ensure PDF.js is loaded
    console.log('📚 [PDF Extraction] Loading PDF.js library...');
    await loadPdfJs();
    console.log('✅ [PDF Extraction] PDF.js loaded successfully');
    
    // Convert file to ArrayBuffer
    console.log('🔄 [PDF Extraction] Converting file to ArrayBuffer...');
    const arrayBuffer = await file.arrayBuffer();
    console.log('✅ [PDF Extraction] ArrayBuffer created, size:', arrayBuffer.byteLength, 'bytes');
    
    // Load the PDF document
    console.log('📄 [PDF Extraction] Loading PDF document...');
    const pdf = await window.pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    console.log('✅ [PDF Extraction] PDF loaded successfully');
    console.log('📄 [PDF Extraction] Number of pages:', pdf.numPages);
    
    let fullText = '';
    
    // Extract text from each page
    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
      console.log(`📄 [PDF Extraction] Processing page ${pageNum}/${pdf.numPages}...`);
      const page = await pdf.getPage(pageNum);
      const textContent = await page.getTextContent();
      
      console.log(`📊 [PDF Extraction] Page ${pageNum} has ${textContent.items.length} text items`);
      
      // Combine text items into a single string
      const pageText = textContent.items
        .map((item: any) => item.str)
        .join(' ');
      
      console.log(`✅ [PDF Extraction] Page ${pageNum} extracted ${pageText.length} characters`);
      
      fullText += pageText + '\n';
    }
    
    console.log('✅ [PDF Extraction] Successfully extracted text from all pages');
    console.log('📊 [PDF Extraction] Total extracted text length:', fullText.length, 'characters');
    
    // Show first 500 characters for debugging
    console.log('📝 [PDF Extraction] First 500 characters of extracted text:');
    console.log(fullText.substring(0, 500));
    
    return fullText;
  } catch (error) {
    console.error('❌ [PDF Extraction] PDF text extraction failed:', error);
    throw new Error(`Failed to extract text from PDF: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Process a file for upload - extract text if PDF, return as-is if CSV
 * @param file The file to process
 * @returns Object with file type and content
 */
export async function processFileForUpload(file: File): Promise<{
  type: 'csv' | 'pdf' | 'pdf-binary';
  content: string;
  encoding: 'plain' | 'base64';
  originalFile: File;
}> {
  console.log('🎯 [File Processing] Starting to process file:', file.name);
  console.log('📊 [File Processing] File details:', {
    name: file.name,
    size: `${(file.size / 1024).toFixed(2)} KB`,
    type: file.type,
    lastModified: new Date(file.lastModified).toISOString()
  });
  
  const fileExtension = file.name.toLowerCase().split('.').pop();
  console.log('📄 [File Processing] Detected file extension:', fileExtension);
  
  if (fileExtension === 'pdf') {
    console.log('📄 [File Processing] Processing PDF file...');
    try {
      // Try to extract text from PDF on client side
      console.log('🔄 [File Processing] Attempting client-side PDF text extraction...');
      const textContent = await extractTextFromPdf(file);
      
      console.log('📊 [File Processing] Extraction complete. Validating results...');
      console.log('📊 [File Processing] Text length:', textContent.length);
      console.log('📊 [File Processing] Non-whitespace characters:', textContent.trim().length);
      console.log('📊 [File Processing] Contains garbled characters (�):', textContent.includes('�'));
      
      // Check if extraction was successful (not empty or garbled)
      if (textContent && textContent.trim().length > 10 && !textContent.includes('�')) {
        console.log('✅ [File Processing] PDF text extracted successfully on client side');
        console.log('📝 [File Processing] Sending extracted text to server');
        console.log('📝 [File Processing] Text preview (first 200 chars):', textContent.substring(0, 200));
        
        return {
          type: 'pdf',
          content: textContent,
          encoding: 'plain',
          originalFile: file
        };
      } else {
        console.warn('⚠️ [File Processing] Extracted text appears to be empty or garbled');
        console.log('📝 [File Processing] Problematic content:', textContent.substring(0, 100));
        throw new Error('PDF text extraction resulted in empty or garbled content');
      }
    } catch (error) {
      console.warn('⚠️ [File Processing] Client-side PDF extraction failed:', error);
      console.log('🔄 [File Processing] Falling back to server-side extraction');
      
      // Fallback: Send PDF as base64 for server-side extraction
      console.log('🔄 [File Processing] Converting PDF to base64...');
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          const base64Content = result.split(',')[1];
          console.log('✅ [File Processing] PDF converted to base64');
          console.log('📊 [File Processing] Base64 length:', base64Content.length);
          console.log('📝 [File Processing] Base64 preview (first 100 chars):', base64Content.substring(0, 100));
          resolve(base64Content);
        };
        reader.onerror = (error) => {
          console.error('❌ [File Processing] Failed to convert PDF to base64:', error);
          reject(error);
        };
        reader.readAsDataURL(file);
      });
      
      console.log('📤 [File Processing] Sending PDF binary to server for extraction');
      return {
        type: 'pdf-binary', // Indicates server needs to extract text
        content: base64,
        encoding: 'base64',
        originalFile: file
      };
    }
  } else if (fileExtension === 'csv' || file.type === 'text/csv' || file.type === 'text/plain') {
    console.log('📄 [File Processing] Processing CSV file...');
    
    // Read CSV as text
    const textContent = await file.text();
    console.log('✅ [File Processing] CSV text read successfully');
    console.log('📊 [File Processing] CSV content length:', textContent.length);
    console.log('📝 [File Processing] CSV preview (first 200 chars):', textContent.substring(0, 200));
    
    return {
      type: 'csv', 
      content: textContent,
      encoding: 'plain',
      originalFile: file
    };
  } else {
    console.error('❌ [File Processing] Unsupported file type:', fileExtension);
    throw new Error(`Unsupported file type: ${fileExtension}. Only CSV and PDF files are supported.`);
  }
}